import {createContext, useContext, useState, useMemo, useCallback, useEffect} from 'react';
import {gql} from '@apollo/client';
import {useQuery} from "@apollo/client/react";
import {useFeatureContext} from '../../lib/context/featureContext';
import {getClaims} from '../../components/auth/sessionStore';
import {compose} from '../../lib/compose';
import withAlerts from '../../lib/withAlerts';

export const CustomerContext = createContext();

// If the permissions query hasn't resolved after this long, stop waiting
// silently and surface it - see the timeout effect below for why.
const PERMS_TIMEOUT_MS = 8000;

export const CustomerContextProvider = ({children, alerts}) => {
  const [configState, setConfigState] = useState(null);
  const [permsTimedOut, setPermsTimedOut] = useState(false);
  const [permsErrorShown, setPermsErrorShown] = useState(false);

  const {cid} = getClaims();
  const {feature_customer_mdm, feature_customer_edge_lock, feature_rbac} = useFeatureContext();

  // Stable reference — without this, useQuery in Apollo v4 sees a "new"
  // variables object every render and tears down/resubscribes its
  // observable, which can synchronously re-emit and trigger another
  // render, looping indefinitely right after AuthedApolloProvider
  // flips to 'ready' post-login.
  const customerContextFilters = useMemo(() => ({
    field: 'with.account_configuration',
    value: '1'
  }), []);

  const {data} = useQuery(CUSTOMER_CONTEXT, {
    variables: { filters: customerContextFilters }
  });
  useEffect(() => {
    data && setConfigState(data?.SelfCustomerAccount?.result[0]);
  }, [data]);

  // RBAC: feature_rbac is the dev/rollout killswitch for RBAC itself (not a
  // customer subscription flag - see featureContext.jsx). Skip the query
  // entirely while it's off so no request fires and permsLoading resolves
  // to false immediately, letting hasPermission's bypass take effect with
  // no network round trip.
  const {data: permsData, loading: permsQueryLoading, error: permsQueryError} = useQuery(PERMISSIONS, {
    skip: !feature_rbac
  });

  // permsState is derived directly from the query result on every render,
  // rather than mirrored into separate useState via a useEffect. Round-
  // tripping through useEffect+useState created a one-render gap where
  // Apollo's `loading` had already flipped to false but permsState hadn't
  // been set yet. Harmless for <Can> (a one-frame flicker, self-corrects
  // on the next render), but fatal for RequirePermission: it issues a real
  // router navigation to /unauthorized during that gap, and there's no
  // later render to correct it once the route has already changed. This
  // was the cause of hard-refreshes on permission-gated routes (e.g.
  // /admin) incorrectly bouncing to /unauthorized even when the user held
  // the permission - navigating in-app never hit this because permsState
  // was already populated from an earlier resolution in the same session.
  const permsState = permsData?.UserPermissions?.permissions ?? null;

  // Bounded wait — if the permissions query hangs indefinitely (dropped
  // connection, hung request), don't leave the user staring at a blank
  // nav/page forever with no explanation. Timing out never grants access;
  // hasPermission/hasAnyPermission still fail closed exactly as before.
  // This only stops the silent infinite wait and gives the user something
  // actionable instead.
  useEffect(() => {
    if (!feature_rbac || !permsQueryLoading) return;
    const timer = setTimeout(() => setPermsTimedOut(true), PERMS_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [feature_rbac, permsQueryLoading]);

  // permsError distinguishes "confirmed no access" (permsState resolved to
  // an empty/non-matching array) from "we don't actually know" (the query
  // broke or never returned). Both cases still fail closed identically via
  // hasPermission/hasAnyPermission - this flag only drives the toast below,
  // it does not loosen any permission check anywhere.
  const permsError = feature_rbac && (!!permsQueryError || permsTimedOut);

  // Surface a single toast on error/timeout instead of a silent empty nav -
  // without this, a real backend outage looks identical to a legitimate
  // "you don't have access" state, and the user has no path forward besides
  // guessing to refresh. `alerts` is optional here (tests that render this
  // provider directly, without AlertsProvider in the tree, won't pass it) -
  // guarded with optional chaining so it's a no-op rather than a crash in
  // that context.
  useEffect(() => {
    if (permsError && !permsErrorShown) {
      alerts?.error?.({
        msg: 'Unable to load your permissions. Please refresh the page. If this continues, contact your administrator.',
        id: 'permsLoadError'
      });
      setPermsErrorShown(true);
    }
  }, [permsError, permsErrorShown, alerts]);

  const hasPermission = useCallback((permission) => {
    if (!feature_rbac) return true; // RBAC not live yet - don't block anyone
    if (!permsState) return false;
    return permsState.includes(permission);
  }, [permsState, feature_rbac]);

  const hasAnyPermission = useCallback((permissions) => {
    if (!feature_rbac) return true;
    if (!permsState) return false;
    return permissions.some(p => permsState.includes(p));
  }, [permsState, feature_rbac]);

  const contextValue = useMemo(() => ({
    mdm_tenant_id: configState?.account_configuration?.mdm_tenant_id,
    mdm_client_id: configState?.account_configuration?.mdm_client_id,
    mdm_client_secret: configState?.account_configuration?.mdm_client_secret,
    mdm_lostmode_msg: configState?.account_configuration?.mdm_lostmode_msg,
    mdm_lostmode_phone: configState?.account_configuration?.mdm_lostmode_phone,
    co_timer: configState?.account_configuration?.co_timer,
    grace_period: configState?.account_configuration?.grace_period,
    alarm_freq: configState?.account_configuration?.alarm_freq,
    description: configState?.description,
    name: configState?.name,
    id: cid,
    rca: cid === configState?.root,
    perms: permsState,
    hasPermission,
    hasAnyPermission,
    // never "loading" when RBAC is off, since the query never fires
    permsLoading: feature_rbac && permsQueryLoading,
    permsError,
  }), [
    configState,
    permsState,
    hasPermission,
    hasAnyPermission,
    feature_rbac,
    permsQueryLoading,
    permsError,
    feature_customer_mdm,
    feature_customer_edge_lock
  ]);

  return (
    <CustomerContext.Provider value={contextValue}>
      {children}
    </CustomerContext.Provider>
  );
};

const CUSTOMER_CONTEXT = gql`
query getCustomerConfig($filters: GenericFilters) {
  SelfCustomerAccount(filters: $filters) {
    result {
      description
      name
      root
      account_configuration {
        mdm_lostmode_msg
        mdm_lostmode_phone
        mdm_client_id
        mdm_client_secret
        mdm_tenant_id
        co_timer
        grace_period
        alarm_freq
      }
    }
  }
}
`;

const PERMISSIONS = gql`
  query getUserPermissions {
    UserPermissions {
      permissions
    }
  }
`;

export default compose(withAlerts)(CustomerContextProvider);

export const useCustomerContext = () => {
  return useContext(CustomerContext);
};
